import { useState } from "react";
import { StepFrame } from "@/features/onboarding/components/StepFrame";
import { NeedsCard } from "@/features/onboarding/components/StudioSteps";
import { useProfileTheme } from "@/features/profiles/hooks/useProfileTheme";
import { ThemeLoadState } from "@/features/profiles/components/ProfileThemeEditor";
import { useCashActivation } from "@/features/billing/hooks/useCashActivation";
import { CashActivationPanel } from "@/features/billing/components/CashActivationPanel";
import { getMyActivationEntitlement } from "@/features/billing/api/entitlementsApi";
import { publishMyIdentity, publishErrorMessage } from "@/features/profiles/api/publishApi";
import { friendlyDataError } from "@/lib/errors";

export function PublishStep({ step, onBack, onPublished }) {
  const t = useProfileTheme();
  const cash = useCashActivation();
  const [status, setStatus] = useState({ state: "idle", message: null });
  const ready = t.load.status === "ready";
  const complete = ready && Boolean(t.load.card && t.load.pp);

  const publish = async () => {
    setStatus({ state: "saving", message: null });
    try {
      if (!(await getMyActivationEntitlement())) {
        await cash.load();
        return setStatus({ state: "error", message: "Your activation isn't active yet, so we couldn't publish. Nothing was changed." });
      }
      const profile = await publishMyIdentity(t.load.card, t.load.pp);
      onPublished({ profile, slug: t.slug, url: t.identity.publicProfileUrl });
    } catch (err) {
      setStatus({ state: "error", message: publishErrorMessage(err, friendlyDataError(err, "We couldn't publish your identity. Please try again.")) });
    }
  };

  let body = <ThemeLoadState t={t} />;
  if (ready && !complete) body = <NeedsCard testId="publish-needs-card" />;
  if (complete) {
    body = (
      <div className="space-y-6">
        <section className="rounded-lg border border-border bg-card p-5 sm:p-6"><CashActivationPanel cash={cash} /></section>
        <section className="rounded-lg border border-border bg-card p-5 sm:p-6">
          <p className="mw-eyebrow">Goes live at</p>
          <p data-testid="publish-profile-url" className="mt-3 break-all font-mono text-sm">{t.identity.publicProfileUrl}</p>
        </section>
      </div>
    );
  }
  return (
    <StepFrame step={step} onBack={onBack} onNext={publish} nextLabel="Publish My MetWorth" busy={status.state === "saving"} busyLabel="Publishing" nextDisabled={!complete || cash.phase !== "activated"} status={status}>
      {body}
    </StepFrame>
  );
}
